/**
 * A hover/focus Tooltip the keyboard can dismiss (2026-09-16).
 *
 * The minimum-margin tags open their explanation on hover AND focus so the
 * reason is never mouse-only — but a focus-opened tooltip that Escape cannot
 * close traps the reader under it (WCAG 1.4.13 "dismissible"; rc-tooltip
 * does not bind Escape). This wraps AntD `Tooltip` in controlled mode and
 * closes it on Escape WITHOUT moving focus; the next focus or hover reopens
 * it as usual. The child must accept `onKeyDown` (a Tag, a span, a div with
 * `tabIndex`), and keeps any handler it already had.
 */
import { cloneElement, useState } from 'react';
import type { KeyboardEvent, ReactElement, ReactNode } from 'react';
import { Tooltip } from 'antd';

interface DismissibleTooltipProps {
  title: ReactNode;
  children: ReactElement<{ onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void }>;
}

export function DismissibleTooltip({ title, children }: DismissibleTooltipProps) {
  const [open, setOpen] = useState(false);
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape' && open) {
      event.stopPropagation();
      setOpen(false);
    }
    children.props.onKeyDown?.(event);
  };
  return (
    <Tooltip trigger={['hover', 'focus']} title={title} open={open} onOpenChange={setOpen}>
      {cloneElement(children, { onKeyDown })}
    </Tooltip>
  );
}
